import {
  ArticleData,
  ArticleDataContext,
} from 'articleEditor/contexts/articleDataProvider';
import { useTranslationCreate } from 'articleEditor/hooks/useTranslationCreate';
import {
  LanguageContext,
  LanguageContextType,
} from 'lib-knowledge-management-common/contexts/language';
import { LanguageId } from 'lib-knowledge-management-common/graphql';
import { LanguageModel } from 'lib-knowledge-management-common/hooks/useManagementLanguages';
import { useContext } from 'react';

type UseAddTranslationReturn = [
  (locale: LanguageId) => Promise<void>,
  ReturnType<typeof useTranslationCreate>[1],
];

/** Hook to add a translation to an article */
export function useAddTranslation(): UseAddTranslationReturn {
  const { setCurrentLanguage } = useContext(LanguageContext);

  const { id } = useContext(ArticleDataContext);
  const [createTranslation, res] = useTranslationCreate({ articleId: id });

  const addTranslation = async (languageId: LanguageId) => {
    createTranslation({
      languageId,
      onQueryUpdated: () => {
        setCurrentLanguage(languageId);
      },
    });
  };
  return [addTranslation, res];
}

type UseEditorLanguagesReturn = LanguageContextType & {
  translatedLanguages: Array<LanguageModel>;
  availableLanguages: Array<LanguageModel>;
};

export function useEditorLanguages(): UseEditorLanguagesReturn {
  const articleData = useContext(ArticleDataContext);
  const languageData = useContext(LanguageContext);

  return transformArticleLanguageData({
    data: languageData,
    articleData,
  });
}

/**
 * This function transforms the language data of an article. It takes in an
 * object containing the management languages data and the article data. It then
 * creates two new arrays:
 *
 * - `translatedLanguages`: This array contains all the languages in which the
 *   article has been translated. It is created by mapping over the titles of
 *   the article and finding the corresponding language in the enabled
 *   languages.
 * - `availableLanguages`: This array contains all the languages in which the
 *   article has not been translated. It is created by filtering out the
 *   languages from the enabled languages that are already present in the
 *   article's titles.
 *
 * The function returns a new object that contains the original management
 * languages data along with the two new arrays.
 *
 * @returns {UseEditorLanguagesData} The transformed language data for the
 *   article editor.
 */
function transformArticleLanguageData({
  data,
  articleData,
}: {
  data: LanguageContextType;
  articleData: ArticleData;
}): UseEditorLanguagesReturn {
  const { enabledLanguages } = data;

  const translatedLanguages = (
    articleData.translations.draft
      .map((title) => {
        return enabledLanguages.find(
          (lang) => lang.languageId === title.languageId,
        );
      })
      .filter(Boolean) as LanguageModel[]
  ).sort((a, b) => a.description.localeCompare(b.description));

  const availableLanguages = enabledLanguages.filter(
    (language) =>
      !articleData.translations.draft.find(
        (t) => t.languageId === language.languageId,
      ),
  );

  return {
    ...data,
    translatedLanguages,
    availableLanguages,
  };
}
